import React, { useCallback, useState, useRef, useEffect } from 'react';
import { View, Text } from '@upstox/upstox-ui';
import { Props } from './typings';
import * as css from './styles';

const WithTooltip = ({
  children,
  tooltip,
  position = 'Top',
  id,
  dataId,
  trigger = 'hover',
}: Props) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    setIsTooltipVisible(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsTooltipVisible(false);
  }, []);

  const handleClick = useCallback(() => {
    setIsTooltipVisible((oldValue) => !oldValue);
  }, []);

  const updateTooltipPosition = useCallback(() => {
    if (!tooltipRef.current || !triggerRef.current) return;

    const tooltipElement: HTMLElement = tooltipRef.current;
    const triggerElement = triggerRef.current;
    const triggerRect = (triggerElement as HTMLElement).getBoundingClientRect();

    const top = triggerRect.top + window.scrollY;
    const left = triggerRect.left + window.scrollX;

    tooltipElement.style.top = `${top}px`;
    tooltipElement.style.left = `${left}px`;
  }, []);

  useEffect(() => {
    if (isTooltipVisible) {
      updateTooltipPosition();
    }
  }, [isTooltipVisible, updateTooltipPosition]);

  return (
    <View
      ref={triggerRef}
      css={css.container}
      id={id}
      data-id={dataId}
      onMouseEnter={trigger === 'hover' ? handleMouseEnter : undefined}
      onMouseLeave={trigger === 'hover' ? handleMouseLeave : undefined}
      onClick={trigger === 'click' ? handleClick : undefined}
    >
      {children}
      {isTooltipVisible && (
        <View
          ref={tooltipRef}
          css={css[`tooltipPosition${position}`]}
          style={{ position: 'fixed' }}
        >
          <View css={css.tooltip}>
            <Text css={css.tooltipText}>{tooltip}</Text>
          </View>
        </View>
      )}
    </View>
  );
};

export default WithTooltip;
